import { useEffect, useRef, useState, type ReactElement } from 'react';
import { elsterApi, type ElsterCertificate } from '../../lib/api';
import { Button, Card, ErrorNote } from '../ui';
import { useElsterLicense, isLicenseRequired } from '../taxes/elster/LicenseGate';

interface Props {
  companyId: string;
}

function formatDate(iso: string | null): string {
  if (!iso) return '–';
  return new Date(iso).toLocaleDateString('de-DE');
}

function expiryWarning(validUntil: string | null): 'expired' | 'soon' | null {
  if (!validUntil) return null;
  const ms = new Date(validUntil).getTime() - Date.now();
  if (ms < 0) return 'expired';
  if (ms < 30 * 24 * 60 * 60 * 1000) return 'soon';
  return null;
}

export function ElsterSection({ companyId }: Props): ReactElement {
  const { ensureAccepted, Modal: LicenseModal } = useElsterLicense(companyId);

  const [certificates, setCertificates] = useState<ElsterCertificate[]>([]);
  const [configured, setConfigured] = useState(false);
  const [loading, setLoading] = useState(true);
  const [apiError, setApiError] = useState<unknown>(null);

  // Upload form state
  const fileRef = useRef<HTMLInputElement>(null);
  const [label, setLabel] = useState('');
  const [pin, setPin] = useState('');
  const [storePin, setStorePin] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<unknown>(null);

  // Delete confirm
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  function load() {
    setApiError(null);
    elsterApi
      .listCertificates(companyId)
      .then((res) => {
        setCertificates(res.certificates);
        setConfigured(res.configured);
      })
      .catch(setApiError)
      .finally(() => setLoading(false));
  }

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [companyId]);

  async function handleUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!label.trim()) {
      setUploadError(new Error('Bitte gib zuerst einen Bezeichner für das Zertifikat ein.'));
      return;
    }

    // Gate: ensure ELSTER license is accepted before uploading.
    const licenseOk = await ensureAccepted();
    if (!licenseOk) {
      if (fileRef.current) fileRef.current.value = '';
      return;
    }

    setUploadError(null);
    setUploading(true);
    try {
      const base64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const result = reader.result as string;
          const idx = result.indexOf(',');
          resolve(idx >= 0 ? result.slice(idx + 1) : result);
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      await elsterApi.uploadCertificate(
        companyId,
        label.trim(),
        base64,
        pin.trim() || undefined,
        storePin && pin.trim() ? true : undefined,
      );
      setLabel('');
      setPin('');
      setStorePin(false);
      load();
    } catch (err) {
      if (isLicenseRequired(err)) {
        // License was revoked or expired between check and upload — re-trigger.
        const accepted = await ensureAccepted();
        if (accepted) void handleUpload(e);
      } else {
        setUploadError(err);
      }
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  }

  async function handleDelete(id: string) {
    setDeletingId(id);
    setApiError(null);
    try {
      await elsterApi.deleteCertificate(companyId, id);
      setDeleteConfirmId(null);
      load();
    } catch (err) {
      setApiError(err);
    } finally {
      setDeletingId(null);
    }
  }

  return (
    <>
      {LicenseModal}
    <Card className="space-y-4">
      <div>
        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">ELSTER-Zertifikat</h3>
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Verwalte deine ELSTER-Softwarezertifikate (.pfx/.p12) für die elektronische Übermittlung von Steuererklärungen.
          {!configured && (
            <span className="ml-1 text-gray-400 dark:text-gray-500">
              ELSTER ist noch nicht freigeschaltet.
            </span>
          )}
        </p>
      </div>

      {loading && <p className="text-sm text-gray-500">Lädt …</p>}
      {apiError ? <ErrorNote error={apiError} /> : null}

      {/* Certificate list */}
      {certificates.length > 0 && (
        <ul className="divide-y divide-gray-100 dark:divide-gray-700">
          {certificates.map((cert) => {
            const warn = expiryWarning(cert.validUntil);
            return (
              <li key={cert.id} className="flex flex-wrap items-center justify-between gap-2 py-3">
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-medium text-gray-900 dark:text-gray-100">{cert.label}</span>
                    {cert.pinStored && (
                      <span className="rounded-full bg-blue-100 px-2 py-0.5 text-xs text-blue-700 dark:bg-blue-900/40 dark:text-blue-300">
                        PIN gespeichert
                      </span>
                    )}
                    {warn === 'expired' && (
                      <span className="rounded-full bg-red-100 px-2 py-0.5 text-xs text-red-700 dark:bg-red-900/40 dark:text-red-300">
                        Abgelaufen
                      </span>
                    )}
                    {warn === 'soon' && (
                      <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">
                        Läuft bald ab
                      </span>
                    )}
                  </div>
                  {cert.subject && (
                    <div className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">{cert.subject}</div>
                  )}
                  <div className="text-xs text-gray-400 dark:text-gray-500">
                    Gültig bis: {formatDate(cert.validUntil)} · Hochgeladen: {formatDate(cert.createdAt)}
                  </div>
                </div>
                <div>
                  {deleteConfirmId === cert.id ? (
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-600 dark:text-gray-300">Wirklich löschen?</span>
                      <Button
                        variant="danger"
                        busy={deletingId === cert.id}
                        onClick={() => { void handleDelete(cert.id); }}
                      >
                        Löschen
                      </Button>
                      <button
                        className="text-xs text-gray-500 hover:underline"
                        onClick={() => setDeleteConfirmId(null)}
                      >
                        Abbrechen
                      </button>
                    </div>
                  ) : (
                    <button
                      className="text-xs font-medium text-red-600 hover:underline dark:text-red-400"
                      onClick={() => setDeleteConfirmId(cert.id)}
                    >
                      Entfernen
                    </button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {/* Upload form */}
      <div className="space-y-3 rounded-xl border border-dashed border-gray-300 p-4 dark:border-gray-600">
        <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Neues Zertifikat hochladen</p>

        <label className="block">
          <span className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
            Bezeichner <span className="text-red-500">*</span>
          </span>
          <input
            type="text"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="z. B. Hauptzertifikat 2025"
            className="w-full rounded-xl border border-gray-300 px-4 py-2.5 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
          />
        </label>

        <label className="block">
          <span className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
            Zertifikats-Passwort
          </span>
          <input
            type="password"
            value={pin}
            onChange={(e) => setPin(e.target.value)}
            placeholder="Passwort Ihrer ELSTER-Zertifikatsdatei"
            className="w-full rounded-xl border border-gray-300 px-4 py-2.5 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
          />
          <span className="mt-1 block text-xs text-gray-400">
            Das Passwort Ihrer Zertifikatsdatei (.pfx) aus dem ELSTER-Portal. Wird benötigt, um die Gültigkeit auszulesen; standardmäßig nicht gespeichert.
          </span>
        </label>

        {pin.trim() && (
          <label className="flex items-center gap-3 rounded-xl border border-gray-200 p-3 dark:border-gray-700">
            <input
              type="checkbox"
              checked={storePin}
              onChange={(e) => setStorePin(e.target.checked)}
              className="h-5 w-5 rounded border-gray-300 text-blue-600"
            />
            <span className="text-sm text-gray-700 dark:text-gray-300">
              PIN für spätere Übermittlungen merken
            </span>
          </label>
        )}

        {uploadError ? <ErrorNote error={uploadError} /> : null}

        <div>
          <input
            ref={fileRef}
            type="file"
            accept=".pfx,.p12,application/x-pkcs12"
            onChange={handleUpload}
            className="hidden"
          />
          <Button
            variant="secondary"
            busy={uploading}
            onClick={() => {
              if (!label.trim()) {
                setUploadError(new Error('Bitte gib zuerst einen Bezeichner ein.'));
                return;
              }
              setUploadError(null);
              fileRef.current?.click();
            }}
          >
            Zertifikatsdatei auswählen (.pfx / .p12)
          </Button>
          <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">
            ELSTER-Softwarezertifikat (.pfx oder .p12), ausgestellt vom ELSTER-Portal.
          </p>
        </div>
      </div>
    </Card>
    </>
  );
}
